import React, {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import './_bookings.scss';
import {FaRegEye} from 'react-icons/fa';
import {Loader, Pagination} from 'components';
import {routes} from 'common';
import {generalExtraActions} from 'reduxStore/actions';
import Filters from 'components/filters/Filters';
import SortBy from 'components/sortBy/SortBy';
import SearchBar from 'components/searchBar/SearchBar';
import {BOOKING_STATUS, BOOKING_STATUS_LABEL} from "../../common/constants";
import {getPaymentMethodDisplay, getStatusBadgeClass, getStatusDisplayLabel} from "./helpers";
import {formatPrice} from 'common/utils';

const filterOptions = [
  {label: BOOKING_STATUS_LABEL[BOOKING_STATUS.PENDING], value: BOOKING_STATUS.PENDING},
  {label: BOOKING_STATUS_LABEL[BOOKING_STATUS.CONFIRMED], value: BOOKING_STATUS.CONFIRMED},
  {label: BOOKING_STATUS_LABEL[BOOKING_STATUS.COMPLETED], value: BOOKING_STATUS.COMPLETED},
  {label: BOOKING_STATUS_LABEL[BOOKING_STATUS.DECLINED], value: BOOKING_STATUS.DECLINED},
  {label: BOOKING_STATUS_LABEL[BOOKING_STATUS.CANCELLED], value: BOOKING_STATUS.CANCELLED},
];

const typeFilterOptions = [
  {label: 'All', value: 'all'},
  {label: 'Bookings', value: 'booking'},
  {label: 'Challenges', value: 'challenge'},
];

const sortOptions = [
  {value: '-created', label: 'Date Added (Newest)'},
  {value: 'created', label: 'Date Added (Oldest)'},
  {value: '-modified', label: 'Date Modified (Newest)'},
  {value: 'modified', label: 'Date Modified (Oldest)'},
  {value: 'date', label: 'Booking Date (Earliest)'},
  {value: '-date', label: 'Booking Date (Latest)'},
  {value: 'price', label: 'Price (Lowest)'},
  {value: '-price', label: 'Price (Highest)'},
];

function Bookings() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {bookings, bookingsLoading, bookingsError, bookingsPagination} = useSelector((state) => state.general);

  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Reset page to 1 when filter, search, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, typeFilter, search, sortBy, itemsPerPage]);

  // Build API parameters
  const buildApiParams = useCallback(() => {
    const params = {
      page: currentPage,
      page_size: itemsPerPage,
    };
    if (sortBy) {
      params.ordering = sortBy;
    }
    if (filter !== 'all') {
      params.status = filter;
    }
    if (typeFilter !== 'all') {
      params.is_challenge = typeFilter === 'challenge';
    }
    if (search) {
      params.search = search;
    }
    return params;
  }, [currentPage, itemsPerPage, sortBy, filter, typeFilter, search]);

  const handleFilter = (status) => {
    setFilter(prev => (prev === status ? 'all' : status));
  };

  const handleTypeFilter = (type) => {
    setTypeFilter(prev => (prev === type ? 'all' : type));
  };

  const handleSearch = (_search) => {
    setSearch(_search);
  };

  const handleSortChange = (_sortBy) => {
    setSortBy(_sortBy);
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (num) => {
    setItemsPerPage(num);
  };

  const handleViewBooking = (bookingId) => {
    navigate(`${routes.BOOKINGS}/${bookingId}`);
  };

  useEffect(() => {
    const params = buildApiParams();
    dispatch(generalExtraActions.getBookings(params));
  }, [dispatch, buildApiParams]);

  const totalItems = bookingsPagination.count;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (bookingsError) {
    return (
      <div className="bookings-page custom-listing">
        <div className="error-message">
          <h3>Error loading bookings</h3>
          <p>{bookingsError?.message || 'Something went wrong. Please try again.'}</p>
          <button onClick={() => {
            const params = buildApiParams();
            dispatch(generalExtraActions.getBookings(params));
          }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bookings-page custom-listing">
      {bookingsLoading && <Loader/>}
      <div className="listing-header">
        <h2 className="listing-title">Bookings</h2>
      </div>
      <div className="listing-filters">
        <div>
          <div className="filter-group">
            <Filters filters={filterOptions} selectedFilter={filter} handleFilter={handleFilter}/>
          </div>
          <div className="filter-group">
            <Filters filters={typeFilterOptions} selectedFilter={typeFilter} handleFilter={handleTypeFilter}/>
          </div>
        </div>
        <div className="filter-group">
          <SortBy options={sortOptions} value={sortBy} onChange={handleSortChange}/>
          <SearchBar value={search} onChange={handleSearch} placeholder="Search bookings..." debounceDelay={500}
                     onDebouncedChange={handleSearch}/>
        </div>
      </div>
      <div className="listing-table-container">
        <table className="listing-table">
          <thead>
          <tr>
            <th>ID</th>
            <th>Type</th>
            <th>User</th>
            <th>Ground</th>
            <th>Date</th>
            <th>Price (PKR)</th>
            <th>Payment Method</th>
            <th>Status</th>
            <th>Cancellation Reason</th>
            <th>Date Created</th>
            <th>Date Modified</th>
            <th>View</th>
          </tr>
          </thead>
          <tbody>
          {bookings.length === 0 ? (
            <tr>
              <td colSpan="11" style={{textAlign: 'center', color: '#888'}}>No bookings found.</td>
            </tr>
          ) : (
            bookings.map((booking, idx) => (
              <tr key={booking.id}>
                <td>{booking.id}</td>
                <td>
                  <span className={`badge ${booking.is_challenge ? 'badge-type-challenge' : 'badge-type-booking'}`}>
                    {booking.is_challenge ? 'Challenge' : 'Booking'}
                  </span>
                </td>
                <td>{booking.user?.full_name || booking.user?.email || 'N/A'}</td>
                <td>{booking.ground_field?.ground?.name || 'N/A'}</td>
                <td>
                  {booking.date ? (
                    booking.slots && booking.slots.length > 0 ? (
                      (() => {
                        // Group slots by date
                        const slotsByDate = {};
                        booking.slots.forEach(slot => {
                          const date = slot.date || booking.date;
                          if (!slotsByDate[date]) {
                            slotsByDate[date] = [];
                          }
                          slotsByDate[date].push(slot);
                        });

                        const dates = Object.keys(slotsByDate).sort();
                        return dates.length > 1 ?
                          `${dates.length} dates` :
                          new Date(dates[0]).toLocaleDateString();
                      })()
                    ) : new Date(booking.date).toLocaleDateString()
                  ) : 'N/A'}
                </td>
                <td>{formatPrice(booking.price || 0)}</td>
                <td>{getPaymentMethodDisplay(booking.payment_method)}</td>
                <td>
                  <span className={`badge ${getStatusBadgeClass(booking.status)}`}>
                    {getStatusDisplayLabel(booking.status)}
                  </span>
                </td>
                <td>
                  {booking.status === BOOKING_STATUS.CANCELLED && booking.cancellation_reason
                    ? booking.cancellation_reason
                    : '—'}
                </td>
                <td>{booking.created ? new Date(booking.created).toLocaleDateString() : 'N/A'}</td>
                <td>{booking.modified ? new Date(booking.modified).toLocaleDateString() : 'N/A'}</td>
                <td>
                  <button
                    className="view-btn"
                    onClick={() => handleViewBooking(booking.id)}
                    title="View booking details"
                  >
                    <FaRegEye/>
                  </button>
                </td>
              </tr>
            ))
          )}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
    </div>
  );
}

export default Bookings;